import { ERROR_CODES } from './errors';
// --- local imports ------

/**
 * What a failed authentication request means for the player.
 *
 * The user system reports very different things through one 401, and the screens
 * need to tell them apart: a lapsed licence has to be explained, an expired token
 * does not, and a backend that is briefly down must not cost the player a valid
 * session at all.
 */
export const AUTH_ERROR_CLASS = {
	// The account or the licence is the problem. Signing in again cannot fix it,
	// so the session is cleared and the reason is put on screen.
	TERMINAL: 'TERMINAL',
	// The session ended and signing in again is the fix. Nothing to explain — the
	// login form the player lands on is the whole answer.
	SESSION: 'SESSION',
	// The user system could not be reached, or failed on its own side. This says
	// nothing about the player's credentials, so the session is kept and the
	// request is retried rather than turned into a logout.
	TRANSIENT: 'TRANSIENT',
	// Firebase refused the credentials themselves. The login form can express this
	// one on its own fields, so it never reaches the error screen.
	CREDENTIALS: 'CREDENTIALS'
};

const TERMINAL_CODES = [
	ERROR_CODES.USER_SYSTEM.ACCOUNT_NOT_FOUND,
	ERROR_CODES.USER_SYSTEM.ACCOUNT_REVOKED,
	ERROR_CODES.USER_SYSTEM.NO_LICENSE,
	ERROR_CODES.USER_SYSTEM.INVALID_LICENSE,
	ERROR_CODES.USER_SYSTEM.LICENSE_EXPIRED
];

const SESSION_CODES = [
	ERROR_CODES.USER_SYSTEM.NO_TOKEN,
	ERROR_CODES.USER_SYSTEM.AUTH_FAILED
];

// Firebase reports a token that expired or was withdrawn through the same
// `auth/` namespace as a mistyped password, but it means the same thing the user
// system's own `AUTH_FAILED` does: sign in again. The form has no field to put
// it under, so it must not be read as a credential the player got wrong.
const FIREBASE_SESSION_CODES = [
	ERROR_CODES.FIREBASE.GENERAL.USER_TOKEN_EXPIRED,
	ERROR_CODES.FIREBASE.GENERAL.INVALID_USER_TOKEN
];

const FIREBASE_CODE_PREFIX = 'auth/';

// A request that never reached the user system carries no status of its own —
// only a name, or a message from the platform. `AbortSignal.timeout` throws
// `TimeoutError`, an abandoned fetch throws `AbortError`, and an offline or
// DNS-level failure arrives as a `TypeError` whose message is the only clue.
const TRANSPORT_ERROR_NAMES = ['TimeoutError', 'AbortError'];
const TRANSPORT_MESSAGE_FRAGMENTS = [
	'Failed to fetch',
	'NetworkError',
	'Network request failed',
	'Load failed'
];

function readStatus(error) {
	if (typeof error?.status === 'number') {
		return error.status;
	}

	// `parseApiResponse` falls the code back to the status when the body carries
	// no code of its own.
	return typeof error?.code === 'number' ? error.code : undefined;
}

/**
 * A request that was cut off before it reported an outcome.
 *
 * Worth telling apart from a refusal: the request may well have been carried out
 * on the other side, so a caller that resubmits it is not repeating a failure —
 * it is repeating something that may already have succeeded.
 */
export function isAbortedRequest(error) {
	return TRANSPORT_ERROR_NAMES.includes(error?.name);
}

function isTransportFailure(error) {
	if (isAbortedRequest(error)) {
		return true;
	}

	const message = typeof error?.message === 'string' ? error.message : '';
	return TRANSPORT_MESSAGE_FRAGMENTS.some((fragment) => message.includes(fragment));
}

/**
 * A role rejection is thrown by the guard chain before any code is attached, so
 * it arrives as a bare 403 and can only be recognised by its status. Exported so
 * the classification and the copy that explains it stay in step.
 */
export function isRoleRejection(error) {
	return readStatus(error) === 403;
}

export function classifyAuthError(error) {
	if (!error) {
		return null;
	}

	const code = error.code;

	if (TERMINAL_CODES.includes(code)) {
		return AUTH_ERROR_CLASS.TERMINAL;
	}

	if (SESSION_CODES.includes(code)) {
		return AUTH_ERROR_CLASS.SESSION;
	}

	// A Firebase call that never landed is a transport failure, not a verdict on
	// the player's credentials.
	if (code === ERROR_CODES.FIREBASE.GENERAL.NETWORK_REQUEST_FAILED) {
		return AUTH_ERROR_CLASS.TRANSIENT;
	}

	if (FIREBASE_SESSION_CODES.includes(code)) {
		return AUTH_ERROR_CLASS.SESSION;
	}

	// A rejected password, a disabled user, an unknown address: the login form has
	// a field to put each of these under.
	if (typeof code === 'string' && code.startsWith(FIREBASE_CODE_PREFIX)) {
		return AUTH_ERROR_CLASS.CREDENTIALS;
	}

	const status = readStatus(error);

	// TRANSIENT is the one class that keeps the player's token and skips the
	// sign-out, and its copy tells the player their account is fine — so it is
	// claimed explicitly, by the two shapes that can honestly claim it, rather
	// than being the class everything unrecognised falls into. A 2xx body with an
	// `error` field, or a bare `TypeError` thrown after a refusal, used to land
	// here and keep credentials that the backend had already rejected.
	if (typeof status === 'number' && status >= 500) {
		return AUTH_ERROR_CLASS.TRANSIENT;
	}

	if (isTransportFailure(error)) {
		return AUTH_ERROR_CLASS.TRANSIENT;
	}

	if (isRoleRejection(error)) {
		return AUTH_ERROR_CLASS.TERMINAL;
	}

	// A 401 with no code we know: the token was refused, so treat it as a session
	// that ended rather than as an account that is broken.
	if (status === 401) {
		return AUTH_ERROR_CLASS.SESSION;
	}

	// Anything else the user system rejected outright. Retrying it would fail the
	// same way, so it is shown rather than retried.
	if (typeof status === 'number' && status >= 400 && status < 500) {
		return AUTH_ERROR_CLASS.TERMINAL;
	}

	// A shape we do not recognise. Treating it as a session that ended costs the
	// player one sign-in; treating it as transient would keep a session the
	// backend may already have refused.
	return AUTH_ERROR_CLASS.SESSION;
}

export function isRetryableAuthError(error) {
	return classifyAuthError(error) === AUTH_ERROR_CLASS.TRANSIENT;
}

export default classifyAuthError;
